import type {
  GenerationEntrySourceSufficiencyDecision,
} from '@/features/chat/services/generation-entry-sufficiency'
import type { AxisName } from '@/features/design-system/source-derived/types'
import type {
  ApplicationGenerationLevel,
  StudioSourceClarificationQuestion,
} from '@/features/source-assessment/source-assessment-contract'
import { isCanonicalAppGenerationSpecRef } from '../../services/app-generation-spec-identity'

export type StudioUserAuthorityProfileId =
  | 'creator-empty'
  | 'design-system-only'
  | 'app-spec-only'
  | 'partial-mixed'
  | 'contract-exact'

export type StudioDesignAuthorityMode =
  | 'creator-default'
  | 'user-selected'
  | 'source-derived'
  | 'pack-release-exact'

export type StudioApplicationAuthorityMode =
  | 'planner-candidate'
  | 'resolved-planner-spec'
  | 'provided-spec'
  | 'partial-provided-spec'
  | 'locked-exact-spec'

export interface StudioGenerationAuthorityEnvelope {
  schemaVersion: 'cogo.studio-generation-authority-envelope.v1'
  stage: 'preflight' | 'final'
  profileId: StudioUserAuthorityProfileId
  subject: {
    projectId: string | null
    tenantId: string | null
    promptRef: string
  }
  sourceSufficiency: GenerationEntrySourceSufficiencyDecision
  designAuthority: {
    mode: StudioDesignAuthorityMode
    authorityRef: string
    evidenceRefs: string[]
    lockedAxes: AxisName[]
    unresolvedAxes: AxisName[]
  }
  applicationAuthority: {
    mode: StudioApplicationAuthorityMode
    appSpecRef: string | null
    evidenceRefs: string[]
    lockedPaths: string[]
    openSlots: string[]
  }
  rights: {
    previewAllowed: boolean
    sameProjectPublicationAllowed: boolean
    packPromotionAllowed: boolean
    receiptRefs: string[]
  }
  questions: StudioSourceClarificationQuestion[]
  claimCeiling: {
    maxGenerationLevel: ApplicationGenerationLevel | null
    exactVisualConformanceRequired: boolean
    sourceReadbackRequired: boolean
  }
}

export interface StudioAuthorityClassificationInput {
  promptRef: string
  projectId: string | null
  tenantId: string | null
  sourceSufficiency: GenerationEntrySourceSufficiencyDecision
  designAuthority: StudioGenerationAuthorityEnvelope['designAuthority']
  applicationAuthority: StudioGenerationAuthorityEnvelope['applicationAuthority']
  rights: StudioGenerationAuthorityEnvelope['rights']
  questions: StudioSourceClarificationQuestion[]
  claimCeiling: StudioGenerationAuthorityEnvelope['claimCeiling']
}

export const STUDIO_GENERATION_DESIGN_AXES = [
  'style',
  'baseColor',
  'theme',
  'chart',
  'radius',
  'font',
] as const satisfies readonly AxisName[]

const DESIGN_AXIS_SET = new Set<string>(STUDIO_GENERATION_DESIGN_AXES)

function isNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function stableUniqueStrings(values: readonly string[]): string[] {
  return [...new Set(values)].sort()
}

function hasOverlap(left: readonly string[], right: readonly string[]): boolean {
  const leftValues = new Set(left)
  return right.some((value) => leftValues.has(value))
}

function hasNonEmptyRefs(refs: readonly string[]): boolean {
  return refs.length > 0 && refs.every(isNonEmptyString)
}

function validateQuestionLimit(
  questions: readonly StudioSourceClarificationQuestion[],
): void {
  if (questions.length > 3) {
    throw new Error('Generation authority accepts at most three clarification questions.')
  }
}

function validateCommonFacts(input: StudioAuthorityClassificationInput): void {
  if (!isNonEmptyString(input.promptRef)) {
    throw new Error('Generation authority requires a stable prompt reference.')
  }
  if (!isNonEmptyString(input.designAuthority.authorityRef)) {
    throw new Error('Generation authority requires a design authority reference.')
  }
  if (
    hasOverlap(
      input.applicationAuthority.lockedPaths,
      input.applicationAuthority.openSlots,
    )
  ) {
    throw new Error('Application authority paths cannot be both locked and open.')
  }
  if (
    input.applicationAuthority.mode !== 'planner-candidate' &&
    !isNonEmptyString(input.applicationAuthority.appSpecRef)
  ) {
    throw new Error('A provided application authority requires a stable AppSpec reference.')
  }
  if (input.rights.packPromotionAllowed) {
    throw new Error('Pack promotion is not admitted by this authority envelope.')
  }
  validateQuestionLimit(input.questions)
  validateSourceDerivedAxes(input.designAuthority)
}

function validateSourceDerivedAxes(
  authority: StudioGenerationAuthorityEnvelope['designAuthority'],
): void {
  const axes = [...authority.lockedAxes, ...authority.unresolvedAxes]
  if (!axes.every((axis) => DESIGN_AXIS_SET.has(axis))) {
    throw new Error('Design authority contains an unknown design axis.')
  }
  if (hasOverlap(authority.lockedAxes, authority.unresolvedAxes)) {
    throw new Error('Design authority axes cannot be both locked and unresolved.')
  }
  if (
    authority.mode === 'source-derived' &&
    (new Set(axes).size !== STUDIO_GENERATION_DESIGN_AXES.length ||
      !STUDIO_GENERATION_DESIGN_AXES.every((axis) => axes.includes(axis)))
  ) {
    throw new Error('Source-derived design authority must account for all six design axes.')
  }
}

function isExactContract(input: StudioAuthorityClassificationInput): boolean {
  return (
    input.designAuthority.mode === 'pack-release-exact' &&
    input.designAuthority.unresolvedAxes.length === 0 &&
    new Set(input.designAuthority.lockedAxes).size ===
      STUDIO_GENERATION_DESIGN_AXES.length &&
    STUDIO_GENERATION_DESIGN_AXES.every((axis) =>
      input.designAuthority.lockedAxes.includes(axis),
    ) &&
    input.applicationAuthority.mode === 'locked-exact-spec' &&
    input.applicationAuthority.openSlots.length === 0 &&
    input.claimCeiling.exactVisualConformanceRequired &&
    input.claimCeiling.sourceReadbackRequired &&
    hasNonEmptyRefs(input.sourceSufficiency.evidenceRefs) &&
    isNonEmptyString(input.designAuthority.authorityRef) &&
    hasNonEmptyRefs(input.designAuthority.evidenceRefs) &&
    isNonEmptyString(input.applicationAuthority.appSpecRef) &&
    hasNonEmptyRefs(input.applicationAuthority.evidenceRefs) &&
    hasNonEmptyRefs(input.rights.receiptRefs)
  )
}

function isResolvedNonDefaultDesign(
  input: StudioAuthorityClassificationInput,
): boolean {
  if (input.designAuthority.mode === 'creator-default') return false
  if (input.designAuthority.mode !== 'source-derived') return true
  return (
    input.designAuthority.unresolvedAxes.length === 0 &&
    input.questions.length === 0
  )
}

export function classifyStudioUserAuthorityProfile(
  input: StudioAuthorityClassificationInput,
): StudioUserAuthorityProfileId {
  validateCommonFacts(input)

  if (isExactContract(input)) return 'contract-exact'
  if (
    input.applicationAuthority.mode === 'partial-provided-spec' ||
    (input.designAuthority.mode === 'source-derived' &&
      (input.designAuthority.unresolvedAxes.length > 0 ||
        input.questions.length > 0))
  ) {
    return 'partial-mixed'
  }
  if (
    input.applicationAuthority.mode === 'provided-spec' &&
    input.designAuthority.mode === 'creator-default'
  ) {
    return 'app-spec-only'
  }
  if (
    (input.applicationAuthority.mode === 'planner-candidate' ||
      input.applicationAuthority.mode === 'resolved-planner-spec') &&
    isResolvedNonDefaultDesign(input)
  ) {
    return 'design-system-only'
  }
  if (
    (input.applicationAuthority.mode === 'planner-candidate' ||
      input.applicationAuthority.mode === 'resolved-planner-spec') &&
    input.designAuthority.mode === 'creator-default'
  ) {
    return 'creator-empty'
  }

  throw new Error('Unsupported Studio generation authority combination.')
}

function normalizeInput(
  input: StudioAuthorityClassificationInput,
): StudioAuthorityClassificationInput {
  return {
    ...input,
    sourceSufficiency: {
      ...input.sourceSufficiency,
      blockerDimensionIds: stableUniqueStrings(
        input.sourceSufficiency.blockerDimensionIds,
      ) as GenerationEntrySourceSufficiencyDecision['blockerDimensionIds'],
      clarificationRequestIds: stableUniqueStrings(
        input.sourceSufficiency.clarificationRequestIds,
      ),
      questions: input.sourceSufficiency.questions.map((question) => ({
        ...question,
        decisionOptions: stableUniqueStrings(question.decisionOptions),
      })),
      evidenceRefs: stableUniqueStrings(input.sourceSufficiency.evidenceRefs),
    },
    designAuthority: {
      ...input.designAuthority,
      evidenceRefs: stableUniqueStrings(input.designAuthority.evidenceRefs),
      lockedAxes: stableUniqueStrings(input.designAuthority.lockedAxes) as AxisName[],
      unresolvedAxes: stableUniqueStrings(input.designAuthority.unresolvedAxes) as AxisName[],
    },
    applicationAuthority: {
      ...input.applicationAuthority,
      evidenceRefs: stableUniqueStrings(input.applicationAuthority.evidenceRefs),
      lockedPaths: stableUniqueStrings(input.applicationAuthority.lockedPaths),
      openSlots: stableUniqueStrings(input.applicationAuthority.openSlots),
    },
    rights: {
      ...input.rights,
      receiptRefs: stableUniqueStrings(input.rights.receiptRefs),
    },
    questions: input.questions.map((question) => ({
      ...question,
      decisionOptions: stableUniqueStrings(question.decisionOptions),
    })),
  }
}

/**
 * Validates both preflight and final receipt invariants. Final receipts cannot
 * retain a planner candidate: a validated AppSpec reference is required before
 * generation evidence can become final.
 */
export function validateStudioGenerationAuthorityEnvelope(
  envelope: StudioGenerationAuthorityEnvelope,
): void {
  const input: StudioAuthorityClassificationInput = {
    promptRef: envelope.subject.promptRef,
    projectId: envelope.subject.projectId,
    tenantId: envelope.subject.tenantId,
    sourceSufficiency: envelope.sourceSufficiency,
    designAuthority: envelope.designAuthority,
    applicationAuthority: envelope.applicationAuthority,
    rights: envelope.rights,
    questions: envelope.questions,
    claimCeiling: envelope.claimCeiling,
  }
  const profileId = classifyStudioUserAuthorityProfile(input)
  if (envelope.profileId !== profileId) {
    throw new Error('Generation authority profile does not match its facts.')
  }
  if (envelope.stage === 'preflight') {
    if (envelope.applicationAuthority.mode === 'resolved-planner-spec') {
      throw new Error('A resolved planner AppSpec is final authority, not preflight authority.')
    }
    return
  }
  if (envelope.applicationAuthority.mode === 'planner-candidate') {
    throw new Error(
      'Final generation authority cannot retain a planner candidate; it requires resolved AppSpec authority.',
    )
  }
  if (
    !isCanonicalAppGenerationSpecRef(
      envelope.applicationAuthority.appSpecRef,
    )
  ) {
    throw new Error(
      'Final generation authority requires a canonical AppSpec SHA-256 identity.',
    )
  }
}

/** Resolves an in-memory preflight into final, content-addressed authority. */
export function finalizeStudioGenerationAuthority(
  preflight: StudioGenerationAuthorityEnvelope,
  appSpecRef: string,
): StudioGenerationAuthorityEnvelope {
  if (preflight.stage !== 'preflight') {
    throw new Error('Only a preflight generation authority can be finalized.')
  }
  const finalEnvelope: StudioGenerationAuthorityEnvelope = {
    ...preflight,
    stage: 'final',
    applicationAuthority: {
      ...preflight.applicationAuthority,
      mode:
        preflight.applicationAuthority.mode === 'planner-candidate'
          ? 'resolved-planner-spec'
          : preflight.applicationAuthority.mode,
      appSpecRef,
    },
  }
  validateStudioGenerationAuthorityEnvelope(finalEnvelope)
  return finalEnvelope
}

export function buildStudioGenerationAuthorityPreflight(
  input: StudioAuthorityClassificationInput,
): StudioGenerationAuthorityEnvelope {
  const normalized = normalizeInput(input)
  const envelope: StudioGenerationAuthorityEnvelope = {
    schemaVersion: 'cogo.studio-generation-authority-envelope.v1',
    stage: 'preflight',
    profileId: classifyStudioUserAuthorityProfile(normalized),
    subject: {
      projectId: normalized.projectId,
      tenantId: normalized.tenantId,
      promptRef: normalized.promptRef,
    },
    sourceSufficiency: normalized.sourceSufficiency,
    designAuthority: normalized.designAuthority,
    applicationAuthority: normalized.applicationAuthority,
    rights: normalized.rights,
    questions: normalized.questions,
    claimCeiling: normalized.claimCeiling,
  }
  validateStudioGenerationAuthorityEnvelope(envelope)
  return envelope
}
